import { useStoreUtils } from "./utils";
import { useFormat } from "../format";

export const useBalanceStore = () => {
    const { createCurrencyValue } = useStoreUtils();
    const { humanize } = useFormat();
    /**
     * Available balance store names
     * @type {string[]}
     * @private
     */
    const balanceNames = ["balance", "cash_balance", "bonus_balance", "coins_balance"];

    // Keep the frontend aligned with the backend SelectedBalanceType enum values.
    const typeValues = window.appContext?.Config?.selectedBalanceTypes;

    const balanceTypes = {
        [typeValues.cash]: {
            key: "balance",
            icon: window.appContext?.Config?.sweepBalanceAssets?.cash,
            alt: "Sweep Coin",
            suffix: "SC",
        },
        [typeValues.coins]: {
            key: "coins_balance",
            icon: window.appContext?.Config?.sweepBalanceAssets?.coins,
            alt: "Gold Coin",
            suffix: "GC",
        },
    };

    // SC/GC are custom coin labels, so format them as numbers plus suffixes.
    const formattedValue = (type, notation = "standard") => {
        const balanceType = balanceTypes[type];

        const value = window.Alpine.store(balanceType.key)?.value ?? 0;

        const formatted = notation === "compact"
            ? humanize(value)
            : value.toLocaleString(window.appContext?.Config?.locale?.current ?? "en", {
                maximumFractionDigits: 2,
            });

        return `${formatted} ${balanceType.suffix}`;
    };

    /**
     * Initializes balance stores for different balance types
     * Creates stores with currency formatting support
     */
    const init = () => {
        balanceNames.forEach((b) => {
            window.Alpine.store(b, createCurrencyValue());
            window.Alpine.store(b).value =
                window.appContext?.Player?.[b] ?? "0";
        });

        if (!window.appContext?.Player) {
            return;
        }

        // Shared state for every sweep balance selector rendered on the page.
        window.Alpine.store("sweep_balance", {
            selected: String(window.appContext.Player.selected_balance_type),
            saving: false,

            get options() {
                return Object.fromEntries(Object.keys(balanceTypes).map((type) => {
                    const balanceType = balanceTypes[type];

                    return [type, {
                        icon: balanceType.icon,
                        alt: balanceType.alt,
                        buttonLabel: formattedValue(type, "compact"),
                        label: formattedValue(type),
                        unplayed: formattedValue(type),
                        redeemable: formattedValue(type),
                    }];
                }));
            },

            get current() {
                return this.options[this.selected];
            },

            async select(type) {
                const next = String(type);

                if (this.selected === next || this.saving) {
                    return;
                }

                const previous = this.selected;
                this.selected = next;
                this.saving = true;

                try {
                    const response = await fetch(window.appContext?.Config?.routes?.balancePreference, {
                        method: "PUT",
                        headers: {
                            "Accept": "application/json",
                            "Content-Type": "application/json",
                            "X-CSRF-TOKEN": window.appContext?.Config?.csrfToken,
                        },
                        body: JSON.stringify({ selected_balance_type: Number(next) }),
                    });

                    if (!response.ok) {
                        throw new Error("Unable to update balance preference.");
                    }

                    const data = await response.json();

                    this.selected = String(data.selected_balance_type);

                    window.Alpine.store("balance").value = data.balance;

                    window.Alpine.store("cash_balance").value = data.cash_balance;

                    window.Alpine.store("coins_balance").value = data.coins_balance;
                } catch {
                    this.selected = previous;
                } finally {
                    this.saving = false;
                }
            },
        });
    };

    return { init, balanceNames };
};
